import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Plus } from "lucide-react";
import { api, absFile } from "@/lib/api";

const inputCls = "w-full border-b border-[#B69A69]/40 py-2 bg-transparent outline-none";

export function AField({ label, children }) {
  return <div><div className="overline text-[#B69A69] mb-1">{label}</div>{children}</div>;
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/upload", fd);
  return r.data.url;
}

export function ImageField({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadFile(file)); toast.success("Uploaded, remember to save"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div data-testid={testid}>
      <div className="overline text-[#B69A69] mb-1">{label}</div>
      <div className="flex gap-3 items-center">
        <div className="w-14 h-14 bg-[#E8D9C5] shrink-0 overflow-hidden">{value && <img src={absFile(value)} alt="" className="w-full h-full object-cover" />}</div>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste a link or upload" className={inputCls} />
        <button type="button" onClick={() => ref.current?.click()} data-testid={testid ? `${testid}-upload` : undefined} className="overline border border-[#B69A69]/40 px-3 py-2 inline-flex gap-1 items-center shrink-0">
          <Upload size={12} />{busy ? "…" : "Upload"}
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-[#522C36] shrink-0" aria-label="Clear image"><Trash2 size={14} /></button>}
        <input ref={ref} type="file" accept="image/*" hidden onChange={onFile} />
      </div>
    </div>
  );
}

const SLOTS = [
  ["hero_1", "Home hero: main photo"],
  ["hero_2", "Home hero: second photo"],
  ["story_1", "Story scrapbook: 'where it all began'"],
  ["story_2", "Story scrapbook: 'the best-friend era'"],
  ["story_3", "Story scrapbook: 'the plot twist'"],
];

export function MediaTab() {
  const [photos, setPhotos] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setPhotos(r.data.couple_photos || {})); }, []);
  if (!photos) return <div>Loading…</div>;
  const save = async () => {
    try { await api.put("/admin/settings", { data: { couple_photos: photos } }); toast.success("Photos saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div data-testid="media-tab" className="max-w-3xl">
      <h1 className="font-serif-display text-3xl mb-2">Couple photos</h1>
      <p className="text-sm text-[#292524]/60 mb-8">Empty slots show a styled placeholder frame on the site.</p>
      <div className="space-y-6">
        {SLOTS.map(([k, l]) => <ImageField key={k} label={l} testid={`photo-${k}`} value={photos[k]} onChange={(v) => setPhotos({ ...photos, [k]: v })} />)}
      </div>
      <button data-testid="save-photos" onClick={save} className="mt-8 bg-[#522C36] text-[#F8F5F0] overline px-6 py-3">Save photos</button>
    </div>
  );
}

function CrudList({ title, endpoint, blank, render, testid }) {
  const [list, setList] = useState([]);
  const refresh = () => api.get(endpoint).then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);
  const save = async (item) => {
    const { id, created_at, ...body } = item;
    try {
      if (id) await api.put(`${endpoint}/${id}`, body); else await api.post(endpoint, body);
      toast.success("Saved"); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (item) => { if (!window.confirm("Delete this item?")) return; await api.delete(`${endpoint}/${item.id}`); toast.success("Deleted"); refresh(); };
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-3xl">{title}</h1>
        <button data-testid={`${testid}-add`} onClick={() => setList([...list, { ...blank, order: list.length + 1 }])} className="bg-[#522C36] text-[#F8F5F0] overline px-4 py-2 inline-flex items-center gap-2"><Plus size={14} />Add</button>
      </div>
      <div className="space-y-6">
        {list.map((item, i) => (
          <Editable key={item.id || `new-${i}`} item={item} render={render} onSave={save} onDelete={() => (item.id ? del(item) : setList(list.filter((_, j) => j !== i)))} />
        ))}
      </div>
    </div>
  );
}

function Editable({ item, render, onSave, onDelete }) {
  const [f, setF] = useState(item);
  return (
    <div className="border border-[#E8D9C5] p-6 space-y-4">
      {render(f, setF)}
      <div className="flex justify-end gap-3">
        <button onClick={onDelete} className="overline text-[#522C36] px-3 py-2">Delete</button>
        <button onClick={() => onSave(f)} className="bg-[#522C36] text-[#F8F5F0] overline px-5 py-2">Save</button>
      </div>
    </div>
  );
}

export function StoryTab() {
  return (
    <CrudList title="Our story episodes" endpoint="/admin/story" testid="story-tab" blank={{ title: "", description: "", image_url: "" }}
      render={(f, setF) => (<>
        <div className="grid md:grid-cols-[1fr_100px] gap-4">
          <AField label="Title"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} /></AField>
          <AField label="Order"><input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} className={inputCls} /></AField>
        </div>
        <AField label="Text"><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full border border-[#B69A69]/40 p-3 bg-transparent min-h-[80px]" /></AField>
        <ImageField label="Photo" value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} />
      </>)} />
  );
}

export function FaqTab() {
  return (
    <CrudList title="FAQs" endpoint="/admin/faqs" testid="faq-tab" blank={{ question: "", answer: "" }}
      render={(f, setF) => (<>
        <div className="grid md:grid-cols-[1fr_100px] gap-4">
          <AField label="Question"><input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} className={inputCls} /></AField>
          <AField label="Order"><input type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} className={inputCls} /></AField>
        </div>
        <AField label="Answer"><textarea value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} className="w-full border border-[#B69A69]/40 p-3 bg-transparent min-h-[70px]" /></AField>
        <AField label="Only show to guests of (event slugs, comma separated; blank = everyone)"><input data-testid="faq-event-slugs" value={(f.event_slugs || []).join(", ")} onChange={(e) => setF({ ...f, event_slugs: e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean) })} placeholder="e.g. mehendi, sangeet" className={inputCls} /></AField>
      </>)} />
  );
}

export function GalleryTab() {
  const [list, setList] = useState([]);
  const [events, setEvents] = useState([]);
  const [caption, setCaption] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const refresh = () => api.get("/admin/gallery").then((r) => setList(r.data));
  useEffect(() => { refresh(); api.get("/admin/events").then((r) => setEvents(r.data)); }, []);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        await api.post("/admin/gallery", { url, caption, event_slug: slug, visible: true });
      }
      toast.success(`Added ${files.length} photo${files.length > 1 ? "s" : ""}`); setCaption(""); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const del = async (g) => { if (!window.confirm("Remove this photo from the gallery?")) return; await api.delete(`/admin/gallery/${g.id}`); refresh(); };
  return (
    <div data-testid="gallery-tab">
      <h1 className="font-serif-display text-3xl mb-6">Gallery</h1>
      <div className="border border-[#E8D9C5] p-6 grid md:grid-cols-[1fr_200px_auto] gap-4 items-end mb-8">
        <AField label="Caption (optional)"><input value={caption} onChange={(e) => setCaption(e.target.value)} className={inputCls} /></AField>
        <AField label="Celebration"><select value={slug} onChange={(e) => setSlug(e.target.value)} className={inputCls}><option value="">General</option>{events.map((ev) => <option key={ev.slug} value={ev.slug}>{ev.name}</option>)}</select></AField>
        <button data-testid="gallery-upload" onClick={() => ref.current?.click()} className="bg-[#522C36] text-[#F8F5F0] overline px-5 py-3 inline-flex items-center gap-2"><Upload size={14} />{busy ? "Uploading…" : "Upload photos"}</button>
        <input ref={ref} type="file" accept="image/*" multiple hidden onChange={onFiles} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {list.map((g) => (
          <div key={g.id} className="relative group" data-testid={`admin-gallery-${g.id}`}>
            <img src={absFile(g.url)} alt={g.caption} className="w-full aspect-square object-cover" />
            <div className="text-xs mt-1 truncate">{g.caption || g.event_slug || "—"}</div>
            <button onClick={() => del(g)} data-testid={`admin-gallery-delete-${g.id}`} className="absolute top-2 right-2 bg-[#F8F5F0] p-1.5" aria-label="Delete photo"><Trash2 size={14} /></button>
          </div>
        ))}
        {!list.length && <div className="text-sm text-[#292524]/60">No photos yet.</div>}
      </div>
    </div>
  );
}
